import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { User } from '../../app/data/models/user';
import { FieldValues } from 'react-hook-form';
import api from '../../app/api/api';
import { router } from '../../app/router/Routes';
import { toast } from 'react-toastify';
import { ResetPasswordRequestModel } from '../../app/data/models/accounts/resetPasswordRequestModel';
import { ForgotPasswordRequestModel } from '../../app/data/models/accounts/forgotPasswordRequestModel';
import { ChangePasswordRequestModel } from '../../app/data/models/accounts/changePasswordRequestModel';

import authService from '../../app/services/authService';

export interface AccountState {
  user: User | null;
  passwordReset: boolean | string;
}

// Initialize user from localStorage if available
const storedUser = authService.getUserFromLocalStorage();

const initialState: AccountState = {
  user: storedUser,
  passwordReset: false,
};

export const loginUser = createAsyncThunk<User, FieldValues>(
  'account/loginUser',
  async (data, thunkApi) => {
    try {
      const user = await api.Account.login(data);
      authService.saveUser(user);
      thunkApi.dispatch(setUser(user));
      return user;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error('Login failed. Please try again.');
      return thunkApi.rejectWithValue({ error: error.data });
    }
  }
);

export const resetPassword = createAsyncThunk<
  boolean,
  ResetPasswordRequestModel
>('account/resetPassword', async (data, thunkApi) => {
  try {
    await api.Account.resetPassword(data);
    return true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    toast.error('Password reset failed');
    return thunkApi.rejectWithValue({
      error: error.data,
    });
  }
});

export const forgotPassword = createAsyncThunk<
  boolean,
  ForgotPasswordRequestModel
>('account/forgotPassword', async (data, thunkApi) => {
  try {
    await api.Account.forgotPassword(data);
    toast.success('An email has been sent to reset password.');
    return true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    toast.error('Password reset failed');
    return thunkApi.rejectWithValue({
      error: error.data,
    });
  }
});

export const changePassword = createAsyncThunk<
  boolean,
  ChangePasswordRequestModel
>('account/changePassword', async (data, thunkApi) => {
  try {
    await api.Account.changePassword(data);
    toast.success('Password has been successfully reset.');
    return true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    toast.error('Password reset failed');
    return thunkApi.rejectWithValue({
      error: error.data,
    });
  }
});

export const verifyEmail = createAsyncThunk<
  string,
  { token: string; email: string }
>('account/verifyEmail', async ({ token, email }, thunkApi) => {
  try {
    const encodedToken = encodeURIComponent(token);
    const encodedEmail = encodeURIComponent(email);
    const passwordResetToken = await api.Account.verifyEmail(
      encodedToken,
      encodedEmail
    );
    toast.success('Email verified successfully.');
    return passwordResetToken;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return thunkApi.rejectWithValue({
      error: error.data,
    });
  }
});

export const logoutUser = createAsyncThunk<void, void>(
  'account/logoutUser',
  async (_, thunkApi) => {
    try {
      const { account } = thunkApi.getState() as { account: AccountState };
      const { user } = account;
      if (user) {
        await api.Account.logout({
          email: user.email,
          refreshToken: user.refreshToken,
        });
      }
      // Navigate BEFORE dispatching the logout action
      router.navigate('/');
      thunkApi.dispatch(logout());
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, navigate home
      router.navigate('/');
    }
  }
);

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      localStorage.removeItem('user');

      // Reset overflow properties that might be preventing scrolling
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';

      router.navigate('/');
    },
    resetState: () => initialState,
    setUser: (state, action) => {
      const user = action.payload;
      localStorage.setItem('user', JSON.stringify(user));
      state.user = user;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.rejected, (state) => {
      state.user = null;
      localStorage.removeItem('user');
      toast.error('Session expired - please login again');
      router.navigate('/');
    });
    builder.addMatcher(isAnyOf(loginUser.rejected), (_, action) => {
      console.log(action.payload);
    });
  },
});

export const { logout, setUser, resetState } = accountSlice.actions;
